/**
 * PATH: packages/email-hub/src/feature/components/email-reader/useEmailReaderController.ts
 * FILE_ID: EMAILHUB-EMAILREADER-CONTROLLER-001
 * PURPOSE: Coordinate EmailReader state, effects, actions, and typed leaf props.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInPageFind, useRenderTiming } from '@quotech/ui';
import { triggerComposeFromEmail } from '@quotech/ui/calendar';
import { getSamKanbanApi, resolveKanbanOrgId } from '@quotech/kanban-host';
import { useApiAuth } from '../../../_internal/components/ApiAuthGate';
import { isEmailHubFeatureEnabled } from '../../../_internal/flags';
import { useIsNarrowViewport } from '../../../_internal/hooks/useIsNarrowViewport';
import { useToast } from '../../../_internal/components/Toast';
import {
  getQuotechOrgIdOverride,
  getRuntimeQuotechOrgId,
} from '../../../_internal/services/quotechApi';
import { useEmail, useEmailActions } from '../../hooks/useEmails';
import { useInlineImageRecovery } from '../../hooks/useInlineImageRecovery';
import { buildKanbanBoardLink, buildKanbanCardLink } from '../../lib/kanbanDeepLink';
import { buildComposeFromEmailPayload } from '../../lib/composeFromEmail';
import { captureQuoteSelection } from '../../lib/quoteSelection';
import { formatResurfaceTime } from '../../lib/resurfaceTimes';
import { emailPath } from '../../lib/basePath';
import { seedFromIntent, useComposeDockStore } from '../../compose/composeDockStore';
import { buildDraftReplyPending, useComposePendingStore } from '../../stores/composePendingStore';
import type { EmailReaderProps, ReaderToolbarActionId } from './types';
import { useReaderShortcuts } from './useReaderShortcuts';
import { useReaderZoom } from './useReaderZoom';

export function useEmailReaderController(props: EmailReaderProps) {
  const {
    emailId,
    onBack,
    backLabel = 'Back to inbox',
    onArchived,
    onTrashed,
    showAISidebar = true,
    hideJobLink = false,
    onSwitchEmail,
    orderedEmailIds,
    allocationMembers,
    hideSenderIdentity = false,
  } = props;
  useRenderTiming('EmailReader', { deps: [emailId] });
  const navigate = useNavigate();
  const isNarrow = useIsNarrowViewport();
  // ONE owner for "where do the related-email sections live". Both consumers —
  // ReaderRail (drops them) and the header adapter (grows the chips that
  // replace them) — read this same boolean, because a rail that dropped them
  // without a chip loses the information and a chip beside a rail that kept
  // them duplicates it.
  //
  // Every narrow width, phones included. This was tablet-only until 2026-08-14
  // on the theory that a phone rail sits directly under the message and reads
  // fine there, so the chips were not worth truncating the sender name for. On a
  // real phone that rail is below the message BODY — hundreds of pixels of
  // scrolling away, under the AI section — and the operator asked for the chips
  // there explicitly ("the synced from and other emails chip button should be
  // there in the mobile screen, similar to tab screen"). The name pays for it:
  // it is `truncate` with a title, and its full value is one tap away in the
  // recipient-details disclosure, whereas a section nobody scrolls to is not
  // reachable at all.
  //
  // Gated on the whole-header flag: the chips live in that header's identity
  // band, so with the flag OFF (legacy ReaderHeader) there is nowhere to put
  // them and the rail must keep its own sections.
  const relatedContextInHeader =
    isNarrow && isEmailHubFeatureEnabled('wholeReaderHeader');
  const { session } = useApiAuth();
  const { toast } = useToast();
  const kanbanOrgId = resolveKanbanOrgId(session, {
    runtimeId: getRuntimeQuotechOrgId(),
    buildtimeId: getQuotechOrgIdOverride(),
  });
  const kanbanApi = useMemo(() => getSamKanbanApi(), []);
  const canRequestRule =
    typeof kanbanApi.proposeRuleRequest === 'function' &&
    typeof kanbanApi.applyRuleRequest === 'function' &&
    kanbanOrgId != null;
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [peekJob, setPeekJob] = useState<number | null>(null);
  const [peekBoardOpen, setPeekBoardOpen] = useState(false);
  const [pingOpen, setPingOpen] = useState(false);
  const zoom = useReaderZoom();

  // Email→Quote ARC § 3.3: keep the cached header visible while the detail
  // request performs the commit-then-fill body hydration.
  const { email, loading, bodyLoading, error, refetch } = useEmail(emailId || null);
  const { toggleStar, archive, markAsRead, bulkAction } = useEmailActions();
  const { bodyRef: inlineImgBodyRef } = useInlineImageRecovery({
    emailId: emailId || null,
    refetch,
  });
  const findBodyRef = useRef<HTMLDivElement | null>(null);
  const combinedBodyRef = useCallback(
    (element: HTMLDivElement | null) => {
      findBodyRef.current = element;
      (inlineImgBodyRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
    },
    [inlineImgBodyRef]
  );
  const find = useInPageFind({
    containerRef: findBodyRef,
    id: 'email-reader',
    label: 'email',
    enabled: !!email,
  });

  const openPing = useCallback(() => setPingOpen(true), []);
  useReaderShortcuts({ emailId, navigate, onSwitchEmail, openPing });
  const sortedToolbarActions = useMemo(
    () =>
      (
        [
          'reply',
          'reply_all',
          'forward',
          'schedule',
          ...(hideJobLink ? [] : ['link']),
          'snooze',
          'archive',
          'trash',
          'star',
        ] as ReaderToolbarActionId[]
      ).map((id) => ({ id })),
    [hideJobLink]
  );
  const trackToolbarAction = useCallback(
    (_id: ReaderToolbarActionId) => undefined,
    []
  );

  const openCompose = useCallback(
    (intent: 'reply' | 'reply_all' | 'forward') => {
      if (!emailId) return;
      if (intent !== 'forward') {
        captureQuoteSelection(String(emailId), findBodyRef.current);
      }
      useComposeDockStore.getState().openOrRestore(seedFromIntent(intent, String(emailId)));
    },
    [emailId]
  );

  const schedule = useCallback(() => {
    if (email) {
      triggerComposeFromEmail(buildComposeFromEmailPayload(email), navigate);
    }
  }, [email, navigate]);

  const snooze = useCallback(
    (iso: string) => {
      if (!emailId) return;
      void bulkAction([emailId], 'snooze', { snoozed_until: iso })
        .then(() => {
          toast(`Snoozed until ${formatResurfaceTime(iso)}.`, 'success');
          (onArchived ?? onBack)();
        })
        .catch(() => toast('Could not snooze this email - try again.', 'error'));
    },
    [bulkAction, emailId, onArchived, onBack, toast]
  );

  const archiveEmail = useCallback(() => {
    if (!emailId) return;
    void archive(emailId)
      .then(() => (onArchived ?? onBack)())
      .catch(() => toast('Could not archive this email - try again.', 'error'));
  }, [archive, emailId, onArchived, onBack, toast]);

  const completeEmail = useCallback(() => {
    if (!emailId || !email) return;
    void archive(emailId, {
      folder: email.folder,
      isRead: email.is_read,
      isStarred: email.is_starred,
      complete: true,
    })
      .then(() => {
        toast('Done - archived and linked card completed', 'success');
        (onArchived ?? onBack)();
      })
      .catch(() => toast('Could not mark this email done - try again.', 'error'));
  }, [archive, email, emailId, onArchived, onBack, toast]);

  const trashEmail = useCallback(() => {
    if (!emailId) return;
    void bulkAction([emailId], 'delete')
      .then(() => (onTrashed ?? onBack)())
      .catch(() => toast('Could not delete this email - try again.', 'error'));
  }, [bulkAction, emailId, onBack, onTrashed, toast]);

  const toggleEmailStar = useCallback(() => {
    if (!emailId || !email) return;
    void toggleStar(emailId, email.is_starred)
      .then(() => refetch())
      .catch(() => toast('Could not update star - try again.', 'error'));
  }, [email, emailId, refetch, toast, toggleStar]);

  const toggleEmailRead = useCallback(() => {
    if (!emailId || !email) return;
    void bulkAction([emailId], email.is_read ? 'mark_unread' : 'mark_read')
      .then(() => refetch())
      .catch(() => toast('Could not update read state - try again.', 'error'));
  }, [bulkAction, email, emailId, refetch, toast]);

  const switchEmail = useCallback(
    (targetId: string) => {
      if (onSwitchEmail) onSwitchEmail(targetId);
      else navigate(emailPath(`/${targetId}`));
    },
    [navigate, onSwitchEmail]
  );

  const openAllocatedCard = useCallback(() => {
    const link = buildKanbanCardLink(email?.allocation_summary);
    if (link) navigate(link);
  }, [email?.allocation_summary, navigate]);

  const openAllocatedBoard = useCallback(() => {
    if (email?.allocation_summary?.board_id) {
      setPeekBoardOpen(true);
      return;
    }
    const link = buildKanbanBoardLink(email?.allocation_summary);
    if (link) navigate(link);
  }, [email?.allocation_summary, navigate]);

  const openPeekedJob = useCallback(() => {
    if (peekJob == null) return;
    setPeekJob(null);
    navigate(`/jobs/${peekJob}`);
  }, [navigate, peekJob]);

  const openPeekedBoard = useCallback(() => {
    setPeekBoardOpen(false);
    const link = buildKanbanBoardLink(email?.allocation_summary);
    if (link) navigate(link);
  }, [email?.allocation_summary, navigate]);

  const onAIAction = useCallback(
    (action: string) => {
      if (!email) return;
      if (action === 'link_job') setShowLinkModal(true);
      if (action === 'create_job') {
        const prefill = [
          email.subject ? `Subject: ${email.subject}` : '',
          email.from_name || email.from_address
            ? `From: ${email.from_name || ''} <${email.from_address}>`
            : '',
          '',
          email.body_text || email.body_preview || '',
        ]
          .filter(Boolean)
          .join('\n');
        navigate(
          `/jobs?${new URLSearchParams({
            create: '1',
            prefill_text: prefill,
          }).toString()}`
        );
      }
      if (action === 'reply') openCompose('reply');
    },
    [email, navigate, openCompose]
  );

  const useDraftReply = useCallback(
    (draft: { subject: string; body: string }) => {
      if (!email) return;
      useComposePendingStore.getState().setPending(buildDraftReplyPending(String(email.id), draft));
      useComposeDockStore.getState().openOrRestore(seedFromIntent('reply', String(email.id)));
    },
    [email]
  );

  const markedReadRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (email && !email.is_read && emailId && !markedReadRef.current.has(emailId)) {
      markedReadRef.current.add(emailId);
      markAsRead(emailId).catch(() => {});
    }
  }, [email, emailId, markAsRead]);

  return {
    emailId,
    email,
    loading,
    bodyLoading,
    error,
    refetch,
    onBack,
    backLabel,
    showAISidebar,
    hideJobLink,
    hideSenderIdentity,
    allocationMembers,
    orderedEmailIds,
    isNarrow,
    relatedContextInHeader,
    kanbanApi,
    kanbanOrgId: kanbanOrgId ?? null,
    canRequestRule,
    showLinkModal,
    setShowLinkModal,
    ruleDialogOpen,
    setRuleDialogOpen,
    peekJob,
    setPeekJob,
    peekBoardOpen,
    setPeekBoardOpen,
    pingOpen,
    setPingOpen,
    find,
    combinedBodyRef,
    sortedToolbarActions,
    trackToolbarAction,
    openCompose,
    schedule,
    snooze,
    archiveEmail,
    completeEmail,
    trashEmail,
    toggleEmailStar,
    toggleEmailRead,
    switchEmail,
    openAllocatedCard,
    openAllocatedBoard,
    openPeekedJob,
    openPeekedBoard,
    onAIAction,
    useDraftReply,
    ...zoom,
  };
}

export type EmailReaderController = ReturnType<typeof useEmailReaderController>;
